import { createSharedComposable } from '@vueuse/core'
import { useShipStore } from '@/pinia/ship'
import { useCurrencyStore } from '@/pinia/currency'
import { bankersRoundWithMax, debounce, getPriceByQuantity } from '@/utils/utils'
import { v4 as uuidv4 } from 'uuid'
import { calcShippingFee } from '@/api/cart'
import { useProductDetail } from './useProductDetail'

export const useCalcShip = createSharedComposable(() => {
  const ship = useShipStore()
  const currencyStore = useCurrencyStore()

  const { detailInfo, skuAttributes, expandServices } = useProductDetail()

  const minDeliveryTime = ref(0)
  const maxDeliveryTime = ref(0)
  const shippingConfirmed = ref(false)

  const shipAmount = ref(0)

  const totalItemsAmount = computed(() => {
    let _totalItemsAmount = 0

    const estimatedQuantity = selectedSkuList.value.reduce((pre, cur) => {
      if (cur.skuType === 2) return pre
      return pre + cur.quantity
    }, 0)

    selectedSkuList.value.forEach(item => {
      const price = item.priceRangeList ? getPriceByQuantity(estimatedQuantity, item.priceRangeList) : item.price
      const targetPrice = +bankersRoundWithMax(price * currencyStore.current.rate, 0.01)
      const totalItems = targetPrice * item.quantity
      _totalItemsAmount += totalItems
    })

    return _totalItemsAmount
  })

  const totalAmount = computed(() => totalItemsAmount.value + shipAmount.value)

  const resetCalcShipInfo = () => {
    shipAmount.value = 0
  }

  const selectedProductList = computed(() => {
    return skuAttributes.value.filter(v => v.quantity > 0).map(item => ({
      skuId: item.skuId,
      skuType: 1,
      skuImageUrl: item.skuImageUrl,
      quantity: item.quantity,
      price: item.price,
      priceRangeList: item.priceRangeList
    })) ?? []
  })

  const selectedProductCount = computed(() => selectedProductList.value.reduce((pre, cur) => {
    return pre + cur.quantity
  }, 0))

  const selectedSkuList = computed(() => {
    const hasQuantityServices = expandServices.value.filter(v => v.quantity > 0).map(item => ({
      skuId: item.skuId,
      skuType: 2,
      skuImageUrl: item.skuImageUrl,
      quantity: item.quantity,
      price: item.price,
      priceRangeList: item.priceRangeList
    }))

    return [].concat(selectedProductList.value, hasQuantityServices)
  })

  watch(selectedSkuList, debounce((list) => {
    if (list.length) {
      calcShipFee(list)
    } else {
      resetCalcShipInfo()
    }
  }, 500))

  /**
   * 防止请求竞态导致列表数据展示不正确问题
   */
  let latestRequestId = 0

  const calcShipFee = async (addedSkuList) => {
    if (addedSkuList.length === 0) return

    const currentRequestId = ++latestRequestId

    const params = {
      address: {
        country: ship.country.countryName,
        countryCode: ship.country.countryCode,
        zip: ship.postCode
      },
      currency: currencyStore.current.targetCurrency,
      fillAllUnShip: true, // 详情页固定传true
      details: addedSkuList.map(item => ({
        spuId: detailInfo.value.offerId,
        categoryId: detailInfo.value.categoryId,
        skuId: item.skuId,
        skuType: item.skuType,
        skuQuantity: item.quantity,
        uniqueKey: uuidv4(),
        skuImage: item.skuImageUrl,
        skuSpec: item.skuSpec,
        skuPriceCny: item.price,
        skuFeature: item.skuFeature
      }))
    }

    const { code, data } = await calcShippingFee(params)

    if (code === 200 && currentRequestId === latestRequestId) {
      resetCalcShipInfo()

      const details = data.details

      if (details.length === 0) return

      const shipPackage = details[0].shipPackage // 数组

      let minTime = shipPackage[0].minDeliveryTime
      let maxTime = shipPackage[0].maxDeliveryTime

      let totalShipAmount = 0
      shipPackage.forEach(item => {
        totalShipAmount += Number(item.shipAmount)
        if (minTime > item.minDeliveryTime) {
          minTime = item.minDeliveryTime
        }
        if (maxTime < item.maxDeliveryTime) {
          maxTime = item.maxDeliveryTime
        }
      })

      minDeliveryTime.value = minTime
      maxDeliveryTime.value = maxTime

      shippingConfirmed.value = shipPackage.every(item => item.confirmed)

      shipAmount.value = totalShipAmount
    }
  }

  /**
   * 地址 / 货币修改后需要重新请求物流费
   */
  watch(() => [ship.country, ship.postCode, currencyStore.current], async () => {
    calcShipFee(selectedSkuList.value)
  })

  return {
    selectedProductList,
    selectedSkuList,
    selectedProductCount,
    minDeliveryTime,
    maxDeliveryTime,
    shipAmount,
    totalItemsAmount,
    totalAmount,
    shippingConfirmed,
    resetCalcShipInfo,
    calcShipFee
  }
})
